/// <amd-module name="Search/Code/Components/ItemsList" />

import * as React from "react";
import * as SharedResources from "Search/Shared/Resources";
import { FocusZoneDirection } from "VSSUI/FocusZone";
import { IMenuItem } from "VSSUI/Menu";
import { List, IListItemDetails, ListItem, IListRow, ListSelection } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { CodeResult } from "Search/LegacyWebApi/Contracts/Code";
import { ItemRow } from "Search/Code/Components/ItemRow";
import { IVssPageContext } from "VSS/Platform/Context";
import { isGitType, isTfvcType } from "Search/Code/Utils";
import { routeUrl } from "VSS/Platform/Location";

export interface IItemsListProps {
    items: CodeResult[];

    previewOrientation: string;

    pageContext: IVssPageContext;

    activeItemIndex: number;

    itemsLoading: boolean;

    notifyOnRendered(): void;

    onItemActivation(item: CodeResult): void;

    onItemMenuInvoked(menuItem: IMenuItem): void;

    onItemFileNameClicked(link: string, evt: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLAnchorElement>): void;

    renderLoadingRow(): JSX.Element;
}

export class ItemsList extends React.Component<IItemsListProps, {}> {
    private isRendered = false;
    private selection: ListSelection;
    private itemRef: React.RefObject<List<CodeResult>> = React.createRef<List<CodeResult>>();

    constructor(props: IItemsListProps) {
        super(props);
        this.selection = new ListSelection({ selectOnFocus: false });
        this.selection.select(props.activeItemIndex);
    }

    public render(): JSX.Element {
        const { activeItemIndex, items } = this.props;
        return (
            <List
                ref={this.itemRef}
                className="items-list flex-grow"
                defaultTabbableRow={activeItemIndex}
                selection={this.selection}
                itemProvider={new ArrayItemProvider(items)}
                renderRow={this.rowRenderer}
                onSelect={this.onSelect}
                onActivate={this.onSelect}
                singleClickActivation={true}
                focuszoneProps={{ direction: FocusZoneDirection.Vertical }}
                selectableText={true}
                width="100%"
                ariaLabel={SharedResources.SearchResultsListAriaLabel}/>
        );
    }

    public componentDidUpdate(prevProps: IItemsListProps): void {
        if (prevProps.activeItemIndex !== this.props.activeItemIndex) {
            // Move focus to selected item
            this.selection.select(this.props.activeItemIndex);
            // Scroll to selected item if required
            if (this.itemRef.current) {
                const { state } = this.itemRef.current;
                if (state.lastRendered === this.props.activeItemIndex
                    || (prevProps.activeItemIndex === state.firstRendered
                    && this.props.activeItemIndex === prevProps.activeItemIndex - 1)) {
                    this.itemRef.current.scrollIntoView(this.props.activeItemIndex);
                }
            }
        }
    }

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<CodeResult>) => {
        this.selection.select(listRow.index);
        this.props.onItemActivation(listRow.data);
    }

    private rowRenderer = (index: number, item: CodeResult, details: IListItemDetails<CodeResult>): JSX.Element => {
        const {
            onItemMenuInvoked,
            onItemFileNameClicked,
            previewOrientation,
            renderLoadingRow
        } = this.props;

        const isActive = this.selection.selected(index);
        return (
            <ListItem index={index} details={details} className="search-item-list-row">
                {
                    !item.hasOwnProperty("project")
                        ? renderLoadingRow()
                        : <ItemRow
                            item={item}
                            active={isActive}
                            fileUrl={this.getItemLink(item)}
                            previewOrientation={previewOrientation}
                            onMenuItemInvoked={onItemMenuInvoked}
                            onFileNameClicked={onItemFileNameClicked}
                            onRenderComplete={this.onItemRenderComplete}
                        />
                }
            </ListItem>
        );
    }

    private getItemLink({ path, project, repository, branch, vcType }: CodeResult): string | undefined {
        const routeValues: { [key: string]: string } = { path, project, "vc.GitRepositoryName": repository, _a: "contents" };

        let routeId: string | undefined;
        if (isGitType(vcType)) {
            routeId = "ms.vss-code-web.files-route-git";
            routeValues.version = `GB${branch}`;
        } else if (isTfvcType(vcType)) {
            routeId = "ms.vss-code-web.tfvc-files-route";
            routeValues.version = "T";
        }

        return routeId ? routeUrl(this.props.pageContext, routeId, routeValues) : undefined;
    }

    private onItemRenderComplete = (): void => {
        if (!this.isRendered && !this.props.itemsLoading) {
            this.isRendered = true;
            this.props.notifyOnRendered();
        }
    }
}